import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { LANGUAGES } from "../lib/constants";
import { playOnce, stopOnce, getSingletonAudio } from "../lib/singleAudio";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { FREE_HD_PROFILES } from "../lib/activeVoice";

const AudioContext = createContext(null);

// iPhone/iPad HOME-SCREEN (standalone) web apps: iOS speechSynthesis is
// unreliable there (utterances silently discarded), so read-aloud falls
// back to HD cloud voices played through an <audio> element — which iOS
// standalone handles fine. Android/desktop keep device voices by default.
export const IOS_STANDALONE_HD_FALLBACK = (() => {
  try {
    const ua = navigator.userAgent || "";
    const ios = /iphone|ipad|ipod/i.test(ua) || (/mac/i.test(ua) && navigator.maxTouchPoints > 1);
    const standalone =
      window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches;
    return !!(ios && standalone);
  } catch (_) {
    return false;
  }
})();
const IOS_FALLBACK_HD_VOICE = "brother_warm";

// Any iOS device (Safari tab OR installed app). iOS kills Web Speech
// (speechSynthesis) the moment the app is backgrounded or the screen
// locks — there is no workaround. HTML5 <audio> (the HD cloud path)
// keeps playing in the background, so premium subscribers on iOS get
// routed to HD by default for uninterrupted listening.
export const IS_IOS = (() => {
  try {
    const ua = navigator.userAgent || "";
    return /iphone|ipad|ipod/i.test(ua) || (/mac/i.test(ua) && navigator.maxTouchPoints > 1);
  } catch (_) {
    return false;
  }
})();

// Tiny runtime-built silent WAV (0.4s, 8kHz mono). Looped on the
// singleton <audio> at chapter end so the iOS audio session survives
// the auto-advance gap (navigate → fetch next chapter → play). Without
// it, backgrounded iPhones kill the session between chapters and the
// next chapter "opens but doesn't play".
const SILENT_WAV_URI = (() => {
  try {
    const sr = 8000, n = Math.floor(sr * 0.4);
    const size = 44 + n * 2;
    const buf = new ArrayBuffer(size);
    const v = new DataView(buf);
    const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
    w(0, "RIFF"); v.setUint32(4, size - 8, true); w(8, "WAVE"); w(12, "fmt ");
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true);
    v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, n * 2, true);
    let bin = "";
    const u8 = new Uint8Array(buf);
    for (let i = 0; i < u8.length; i += 8192) bin += String.fromCharCode.apply(null, u8.subarray(i, i + 8192));
    return "data:audio/wav;base64," + btoa(bin);
  } catch (_) { return null; }
})();

function holdSessionWithSilence() {
  if (!SILENT_WAV_URI) return;
  try {
    const el = getSingletonAudio();
    el.ontimeupdate = null;
    el.onended = null;
    el.onerror = null;
    el.loop = true;
    el.src = SILENT_WAV_URI;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    // Nothing took over within 45s (no auto-advance / end of book) —
    // release the session instead of looping silence forever.
    setTimeout(() => {
      try {
        if (el.loop && el.src && el.src.startsWith("data:")) {
          el.loop = false;
          el.pause();
        }
      } catch (_) {}
    }, 45000);
  } catch (_) {}
}

// "Try free voice first" (June 2026): instead of ALWAYS paying for HD
// voices on iOS home-screen apps, we now attempt the FREE device voice
// with a 3s watchdog. If the utterance never actually starts speaking,
// the device is marked "dead" and that session (plus all future ones)
// falls back to HD. Verdict persists per device so working iPhones
// never pay for HD again and broken ones skip the 3s probe next time.
const iosNativeVerdict = () => {
  try { return window.localStorage.getItem("hw:ios:nativeTts") || ""; } catch (_) { return ""; }
};
const setIosNativeVerdict = (v) => {
  try { window.localStorage.setItem("hw:ios:nativeTts", v); } catch (_) {}
};

export function AudioProvider({ children }) {
  // UI / TTS language picker. Persisted to localStorage so a refresh
  // keeps the user's pick. On FIRST visit (no stored value) we sniff
  // the browser's navigator.language and use that as the default if
  // we ship translations for it — so a Spanish-speaking phone lands
  // on the Spanish UI immediately. Falls back to "en" otherwise.
  const [language, _setLanguageState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem("hw:language");
    if (stored) return stored;
    try {
      const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
      // ISO supported in our dictionary. Keep zh-CN as a full locale,
      // strip everything else down to bare ISO ("es-MX" → "es"). Also
      // includes the 8 languages we added for the 18-language lineup
      // (it, pl, tr, he, ja, ko, id, tl) so a phone set to those lands
      // on the right localized UI on first visit.
      const supported = new Set([
        "en","es","fr","de","pt","it","pl","ru","tr","he","ar","hi",
        "zh-CN","ja","ko","id","tl","sw",
      ]);
      const candidate = raw.startsWith("zh") ? "zh-CN"
        : raw.startsWith("fil") ? "tl"  // Filipino's BCP-47 code is "fil"; we store it as "tl"
        : raw.split("-")[0];
      if (supported.has(candidate)) {
        window.localStorage.setItem("hw:language", candidate);
        return candidate;
      }
    } catch (_) {}
    return "en";
  });
  const setLanguage = useCallback((next) => {
    _setLanguageState(next);
    try {
      if (next) window.localStorage.setItem("hw:language", next);
    } catch (_) {}
  }, []);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [queue, setQueue] = useState([]); // [{ id, text, label }]
  const [rate, setRate] = useState(() => {
    if (typeof window === "undefined") return 0.95;
    const stored = parseFloat(window.localStorage.getItem("hw:audio:rate") || "");
    return Number.isFinite(stored) ? stored : 0.95;
  });
  const [meta, setMeta] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURIState] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("hw:audio:voiceURI") || null;
  });
  const [hdVoice, setHdVoiceState] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("hw:audio:hdVoice") || null;
  });
  const utterRef = useRef(null);
  const audioElRef = useRef(null);
  const audioUrlRef = useRef(null);
  const cancelTokenRef = useRef(0);
  // Snapshot of the current playback session — used to re-cue from the
  // current verse when the user picks a different voice mid-playback so
  // the change applies immediately instead of waiting until the next verse.
  const activeSessionRef = useRef(null);
  // Forward refs to speakIndex / speakIndexHd so callbacks defined
  // BEFORE those functions (like setHdVoice, which needs to live-swap
  // playback) can still invoke them.
  const speakIndexRef = useRef(null);
  const speakIndexHdRef = useRef(null);
  // {idx, url} — next verse's audio, pre-materialized for synchronous
  // handoff inside `ended` (iOS background continuity).
  const nextReadyRef = useRef(null);
  // Whole-chapter stream state — see "iOS single-stream" block below.
  const streamRef = useRef(null);       // {token, url, segments}
  const streamingRef = useRef(false);
  const streamReqRef = useRef(0);
  const requestChapterStreamRef = useRef(null);
  const switchToStreamRef = useRef(null);
  const clearNextReady = useCallback(() => {
    const n = nextReadyRef.current;
    if (n && n.url) { try { URL.revokeObjectURL(n.url); } catch (_) {} }
    nextReadyRef.current = null;
  }, []);
  // Forward ref to teardownAudio for the same reason — avoids a TDZ
  // error when setHdVoice's useCallback dependency array evaluates
  // `teardownAudio` before its const declaration runs.
  const teardownAudioRef = useRef(null);
  // Mirrors `currentIndex` state into a ref so live-swap can read the
  // latest verse index without restructuring all the callbacks below.
  const currentIndexRef = useRef(0);
  // Prefetch cache: maps `idx → Promise<Blob>` so the verse after the
  // one currently playing is downloaded in the background. Eliminates
  // the 200–2000 ms silence between verses that made the read sound
  // "skippy" / choppy. Keyed by `${token}:${idx}` so cancelled sessions
  // don't leak their prefetches into the next session.
  const prefetchRef = useRef({});
  // Runaway-scan guard (July 2026 iPhone bug): count verses that ACTUALLY
  // played in this session. Auto-advance (hw:queue:ended) only fires when
  // at least one verse was heard — a device where TTS fails instantly
  // otherwise "finishes" a silent chapter per second and the readers
  // chain-scan through the whole book. failStreak stops a dead session
  // early instead of silently skipping every verse.
  const spokeOkRef = useRef(0);
  const failStreakRef = useRef(0);
  // iOS unlock: the FIRST speechSynthesis.speak() must happen inside the
  // user's tap (especially in installed/standalone web apps, where iOS
  // silently discards anything spoken after an async boundary). We prime
  // a silent utterance synchronously at the top of play() — once per
  // page session — which unlocks the engine for every later utterance,
  // including hands-free chapter chaining.
  const speechPrimedRef = useRef(false);
  // Set by utterance.onstart — proof the free device voice ACTUALLY
  // began speaking (used by the iOS watchdog in play()).
  const speechStartedRef = useRef(false);

  const noteVerseFailure = useCallback(() => {
    failStreakRef.current += 1;
    // 4 straight failures with zero successes = the engine is dead,
    // not a transient hiccup. Returns true when the caller should stop.
    return spokeOkRef.current === 0 && failStreakRef.current >= 4;
  }, []);

  const setVoiceURI = useCallback((uri) => {
    setVoiceURIState(uri || null);
    if (typeof window !== "undefined") {
      if (uri) window.localStorage.setItem("hw:audio:voiceURI", uri);
      else window.localStorage.removeItem("hw:audio:voiceURI");
    }
  }, []);

  const setHdVoice = useCallback((voice) => {
    setHdVoiceState(voice || null);
    if (typeof window !== "undefined") {
      if (voice) window.localStorage.setItem("hw:audio:hdVoice", voice);
      else window.localStorage.removeItem("hw:audio:hdVoice");
      // Tell every Read-Aloud button on the page to drop its cached
      // audio blob so the next tap re-fetches with the new voice.
      try {
        window.dispatchEvent(new CustomEvent("hw:voice:changed", { detail: { voice } }));
      } catch (_) {}
    }
    // Live-swap the voice mid-read: if there's an active TTS session,
    // bump the cancel token so the in-flight request becomes a no-op,
    // tear down the currently-playing <audio> element so the two
    // voices don't overlap, then re-start at the current verse with
    // the new voice. Without this, the old audio keeps playing while
    // the new voice only kicks in on the next verse — the "switching
    // voices and they speak over each other" bug.
    const session = activeSessionRef.current;
    if (!session) return;
    cancelTokenRef.current += 1;
    if (teardownAudioRef.current) teardownAudioRef.current();
    // The voice changed — anything we prefetched in the OLD voice is
    // garbage now. Clear it before kicking off the re-cue.
    prefetchRef.current = {};
    const token = cancelTokenRef.current;
    const startIdx = currentIndexRef.current || 0;
    // We re-start whether the new voice is HD (cloud) or Web Speech —
    // a null `voice` means "fall back to the browser TTS engine".
    setTimeout(() => {
      if (token !== cancelTokenRef.current) return;
      if (voice) {
        speakIndexHdRef.current && speakIndexHdRef.current(startIdx, session.queue, token, voice, session.options?.model);
      } else {
        speakIndexRef.current && speakIndexRef.current(startIdx, session.queue, token, session.langVoice || "en-US");
      }
    }, 60);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("hw:audio:rate", String(rate));
  }, [rate]);

  // HD voices are a PREMIUM perk (owner decision, June 2026) — EXCEPT
  // the two free house profiles (Storytelling Preacher, Warm Brother).
  // If a confirmed non-premium user has a PREMIUM voice stored (old
  // selection before the gate, shared device, localStorage edit),
  // silently reset them. `user === null` means auth is still
  // resolving — never downgrade during that window.
  const { user: authUser } = useAuth();
  const isPremiumUser = !!(authUser && (authUser.is_premium || authUser.is_owner || authUser.role === "owner" || authUser.role === "admin"));
  // iOS → ALWAYS prefer the HD cloud path (owner decision, July 2026):
  // iPhone kills Web Speech (speechSynthesis) the moment the app is
  // backgrounded or the screen locks — no workaround exists. HTML5
  // <audio> keeps playing in the background, so every iOS user gets
  // cloud audio (free brother_warm profile unless they picked another
  // voice). Server-side tts_cache means each verse+voice+speed combo
  // is synthesized once ever, so cost stays bounded.
  const resolveEffVoice = useCallback((hv) =>
    hv
    || (IS_IOS ? IOS_FALLBACK_HD_VOICE : null)
    || (IOS_STANDALONE_HD_FALLBACK && iosNativeVerdict() === "dead" ? IOS_FALLBACK_HD_VOICE : null),
  []);
  useEffect(() => {
    // `null` = auth still resolving, `false` = signed OUT. Never wipe
    // the stored pick in either case — logging out used to reset the
    // user's chosen voice every single time (owner complaint July
    // 2026). Only a SIGNED-IN, confirmed non-premium account gets
    // silently downgraded off premium voices.
    if (!authUser) return;
    if (!isPremiumUser && hdVoice && !FREE_HD_PROFILES.has(hdVoice)) setHdVoice(null);
  }, [authUser, isPremiumUser, hdVoice, setHdVoice]);

  // Load Web Speech voices (async on most browsers)
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const refresh = () => {
      const list = window.speechSynthesis.getVoices() || [];
      setVoices(list);
    };
    refresh();
    window.speechSynthesis.onvoiceschanged = refresh;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const teardownAudio = useCallback(() => {
    const el = audioElRef.current;
    if (el) {
      // Stream-mode leftovers must not leak into per-verse playback.
      try { el.ontimeupdate = null; } catch (_) {}
      streamingRef.current = false;
      // We now share the singleton <audio> element across verses — do
      // NOT clear `el.src` or call `el.load()` here. Those would
      // destroy the gesture-permitted singleton and the next verse
      // would silently fail with NotAllowedError on mobile.
      // `stopOnce()` is the singleton-safe way to halt playback —
      // it pauses, resets currentTime, and revokes the previous
      // blob URL. Per-verse handlers (onended/onerror) are
      // re-attached fresh each verse so there's nothing to detach.
      try { stopOnce(); } catch (_) {}
      // Clear our own reference, but leave the singleton itself
      // alive in the singleAudio module.
      audioElRef.current = null;
    }
    if (audioUrlRef.current) {
      try { URL.revokeObjectURL(audioUrlRef.current); } catch (_) {}
      audioUrlRef.current = null;
    }
    clearNextReady();
  }, [clearNextReady]);
  // Forward-ref wiring so setHdVoice (defined above) can call us
  // without listing `teardownAudio` in its dependency array — that
  // dependency would cause a Temporal Dead Zone error in minified
  // builds where the array is evaluated before this const is bound.
  teardownAudioRef.current = teardownAudio;

  const stop = useCallback(() => {
    cancelTokenRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoading(false);
    setCurrentIndex(0);
    activeSessionRef.current = null;
    streamRef.current = null;
    streamReqRef.current = 0;
    // Drop any in-flight verse prefetches from the cancelled session.
    prefetchRef.current = {};
  }, [teardownAudio]);

  // Sleep timer (June 2026): stop read-aloud after N minutes — bedtime
  // listening without draining the battery (or the HD voice budget).
  const [sleepPreset, setSleepPreset] = useState(0);
  const sleepUntilRef = useRef(null);
  const setSleepTimer = useCallback((minutes) => {
    setSleepPreset(minutes || 0);
    sleepUntilRef.current = minutes ? Date.now() + minutes * 60000 : null;
  }, []);
  useEffect(() => {
    if (!sleepPreset) return;
    const t = setInterval(() => {
      if (sleepUntilRef.current && Date.now() >= sleepUntilRef.current) {
        sleepUntilRef.current = null;
        setSleepPreset(0);
        stop();
        try { toast("Sleep timer — good night. Playback stopped."); } catch (_) {}
      }
    }, 5000);
    return () => clearInterval(t);
  }, [sleepPreset, stop]);

  // iOS keep-alive: Safari pauses long device-voice sessions (the old
  // ~15s speechSynthesis stall) — a periodic resume() nudge keeps the
  // reading going. No-op for HD sessions and on other platforms.
  useEffect(() => {
    if (!isPlaying || isPaused || hdVoice) return;
    if (IOS_STANDALONE_HD_FALLBACK && iosNativeVerdict() === "dead") return;
    const t = setInterval(() => {
      try {
        if (!window.speechSynthesis.paused) window.speechSynthesis.resume();
      } catch (_) {}
    }, 5000);
    return () => clearInterval(t);
  }, [isPlaying, isPaused, hdVoice]);

  // Hard-stop for any speech that might be playing — covers both
  // our cloud TTS audio element AND the Web Speech API utterances
  // fired from Read.jsx / voiceCommander.js. Used by external surfaces
  // (sermons, voice commander, Read page) right before they start
  // their own TTS so two voices never overlap. The `hw:speech:
  // external-start` event below is the broadcast version of the same
  // thing for callers that don't have a direct ref to this context.
  const cancelAllSpeech = useCallback(() => {
    cancelTokenRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoading(false);
    activeSessionRef.current = null;
    prefetchRef.current = {};
  }, [teardownAudio]);

  // Dismiss the player entirely (stops playback AND clears the queue so the
  // floating AudioBar hides itself). Used by the X close button.
  const close = useCallback(() => {
    cancelTokenRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoading(false);
    setCurrentIndex(0);
    setQueue([]);
    setMeta(null);
    activeSessionRef.current = null;
  }, [teardownAudio]);

  const pickVoice = useCallback((langVoice) => {
    // If the user has explicitly picked a voice, ALWAYS honor it — even if
    // its language doesn't match the chapter's locale. Their pick beats our
    // heuristics, otherwise switching voices in the picker has no effect.
    const all = window.speechSynthesis.getVoices() || [];
    if (voiceURI) {
      const v = all.find((x) => x.voiceURI === voiceURI);
      if (v) return v;
    }
    // No user pick → prefer exact lang match, then language family, then any
    const exact = all.find((v) => v.lang?.toLowerCase() === (langVoice || "").toLowerCase());
    if (exact) return exact;
    const lang2 = (langVoice || "").slice(0, 2).toLowerCase();
    return all.find((v) => v.lang?.toLowerCase().startsWith(lang2)) || null;
  }, [voiceURI]);

  // ── iOS background continuity ────────────────────────────────────
  // While the app is backgrounded (user surfing the web / screen off)
  // iOS throttles async work between <audio> clips; if the handoff to
  // the next verse takes too long the audio session is terminated and
  // the chapter stops. Cure: materialize the NEXT verse's blob into an
  // object URL while the current verse is still playing, then hand off
  // SYNCHRONOUSLY inside the `ended` event (src swap + play(), no
  // awaits). Async fetch path stays as the fallback.
  const armNextRef = useRef(null);

  const startVerseUrl = useCallback((idx, url, list, token, voiceId, model) => {
    const el = getSingletonAudio();
    audioElRef.current = el;
    audioUrlRef.current = url;
    delete prefetchRef.current[`${token}:${idx}`];
    el.loop = false;
    el.ontimeupdate = null;
    el.onended = null;
    el.onerror = null;
    el.onended = () => {
      if (token !== cancelTokenRef.current) return;
      try { URL.revokeObjectURL(url); } catch (_) {}
      if (audioUrlRef.current === url) audioUrlRef.current = null;
      const nxt = idx + 1;
      setCurrentIndex(nxt);
      if (switchToStreamRef.current(nxt, list, token, voiceId, model)) return;
      const ready = nextReadyRef.current;
      if (ready && ready.idx === nxt && ready.url) {
        nextReadyRef.current = null;
        startVerseUrlRef.current(nxt, ready.url, list, token, voiceId, model);
      } else {
        speakIndexHdRef.current(nxt, list, token, voiceId, model);
      }
    };
    el.onerror = () => {
      if (token !== cancelTokenRef.current) return;
      try { URL.revokeObjectURL(url); } catch (_) {}
      if (audioUrlRef.current === url) audioUrlRef.current = null;
      if (noteVerseFailure()) {
        setIsPlaying(false); setIsPaused(false); setIsLoading(false);
        try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again."); } catch (_) {}
        return;
      }
      setCurrentIndex(idx + 1);
      speakIndexHdRef.current(idx + 1, list, token, voiceId, model);
    };
    // Synchronous src swap + play — keeps the iOS audio session alive.
    el.src = url;
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        spokeOkRef.current += 1;
        failStreakRef.current = 0;
      }).catch(() => { try { el.onerror && el.onerror(); } catch (_) {} });
    }
    // Materialize the verse after next while this one plays.
    armNextRef.current(idx + 1, list, token, voiceId, model);
  }, [noteVerseFailure]);
  const startVerseUrlRef = useRef(null);
  startVerseUrlRef.current = startVerseUrl;

  const armNext = useCallback((nextIdx, list, token, voiceId, model) => {
    clearNextReady();
    if (nextIdx >= list.length) return;
    const mk = (i) => api.post("/tts/speak", {
      text: list[i].text,
      voice: voiceId,
      model: model || "tts-1",
      speed: rate,
      reverent: !!list[i].red,
    }, { responseType: "blob", timeout: 25000 }).then((r) => r.data);
    const key = `${token}:${nextIdx}`;
    if (!prefetchRef.current[key]) {
      prefetchRef.current[key] = mk(nextIdx).catch(() => null);
    }
    prefetchRef.current[key].then((b) => {
      if (token !== cancelTokenRef.current || !b) return;
      if (nextReadyRef.current && nextReadyRef.current.idx === nextIdx) return;
      clearNextReady();
      nextReadyRef.current = { idx: nextIdx, url: URL.createObjectURL(b) };
    });
    // Pre-warm ONE more verse. Depth is deliberately shallow: on cold
    // chapters the stitch job is synthesizing in parallel, and racing
    // too many simultaneous synths starves the very next verse (the
    // audible multi-second gap users reported).
    if (nextIdx + 1 < list.length) {
      const nn = `${token}:${nextIdx + 1}`;
      if (!prefetchRef.current[nn]) {
        prefetchRef.current[nn] = mk(nextIdx + 1).catch(() => null);
      }
    }
  }, [rate, clearNextReady]);
  armNextRef.current = armNext;

  // ── iOS single-stream mode ───────────────────────────────────────
  // Even a synchronous per-verse handoff leaves N-1 clip boundaries
  // where iOS can end a backgrounded audio session. The definitive
  // fix: while the first verses play per-verse, ask the backend to
  // stitch the WHOLE chapter into one MP3 (+ verse timings). As soon
  // as it's ready we switch to the single file at the next verse
  // boundary — from then on it's one continuous stream (podcast
  // model) that iOS never interrupts. Verse highlight keeps working
  // via `timeupdate` against the segment table.
  const requestChapterStream = useCallback(async (list, token, voiceId, model) => {
    const sess = activeSessionRef.current;
    // Translated sessions mutate queue text mid-play — skip streaming.
    if (sess && sess.options?.translate && sess.langCode && sess.langCode !== "en") return;
    if (!list || list.length < 2) return;
    const body = {
      items: list.map((v) => ({ text: v.text, red: !!v.red })),
      voice: voiceId,
      model: model || "tts-1",
      speed: rate,
    };
    // The stitch builds in the background server-side; poll until the
    // meta lands (idempotent POST). ~40 × 4s ≈ 160s ceiling covers
    // even the longest cold chapters.
    for (let attempt = 0; attempt < 40; attempt++) {
      if (token !== cancelTokenRef.current) return;
      try {
        const { data } = await api.post("/tts/chapter", body, { timeout: 20000 });
        if (token !== cancelTokenRef.current) return;
        if (data && data.url && Array.isArray(data.segments) && data.segments.length === list.length) {
          streamRef.current = { token, url: data.url, segments: data.segments };
          // Warm the HTTP path for the stream file so the switch at the
          // next verse boundary starts without a buffering pause.
          try { fetch(data.url, { headers: { Range: "bytes=0-1" } }).catch(() => {}); } catch (_) {}
          return;
        }
        if (!data || data.status !== "building") return; // unsupported voice etc.
      } catch (err) {
        const code = err?.response?.status;
        if (code && code !== 502 && code !== 504) return; // 409/402/4xx — give up
      }
      await new Promise((r) => setTimeout(r, 4000));
    }
  }, [rate]);
  requestChapterStreamRef.current = requestChapterStream;

  const switchToStream = useCallback((idx, list, token, voiceId, model) => {
    const st = streamRef.current;
    if (!st || st.token !== token || !st.segments[idx]) return false;
    clearNextReady();
    prefetchRef.current = {};
    const el = getSingletonAudio();
    audioElRef.current = el;
    audioUrlRef.current = null;
    streamingRef.current = true;
    el.loop = false;
    el.onended = () => {
      if (token !== cancelTokenRef.current) return;
      const segs = st.segments;
      const last = segs[segs.length - 1];
      // Safety net: if 'ended' fired before we ever reached the last
      // verse (browser misread the stitched duration / stream stalled),
      // don't advance the chapter — resume per-verse from where we were.
      if (el.currentTime && last && el.currentTime < last.start - 1) {
        streamingRef.current = false;
        streamRef.current = null;
        try { el.ontimeupdate = null; } catch (_) {}
        speakIndexHdRef.current(
          Math.min(currentIndexRef.current, segs.length - 1),
          list, token, voiceId, model,
        );
        return;
      }
      streamingRef.current = false;
      try { el.ontimeupdate = null; } catch (_) {}
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoading(false);
      setCurrentIndex(0);
      // Keep the iOS audio session alive through the chapter hop.
      holdSessionWithSilence();
      try {
        window.dispatchEvent(new CustomEvent("hw:queue:ended", { detail: { kind: "hd" } }));
      } catch (_) {}
    };
    el.onerror = () => {
      if (token !== cancelTokenRef.current) return;
      streamingRef.current = false;
      streamRef.current = null;
      try { el.ontimeupdate = null; } catch (_) {}
      // Fall back to per-verse chaining from wherever we were.
      const at = Math.max(idx, currentIndexRef.current);
      speakIndexHdRef.current(at, list, token, voiceId, model);
    };
    el.ontimeupdate = () => {
      if (token !== cancelTokenRef.current || !streamingRef.current) return;
      const t = el.currentTime;
      const segs = st.segments;
      let i = 0;
      for (let k = segs.length - 1; k >= 0; k--) {
        if (t >= segs[k].start - 0.05) { i = k; break; }
      }
      if (i !== currentIndexRef.current) setCurrentIndex(i);
    };
    const startAt = st.segments[idx].start || 0;
    // Media fragment start (#t=) — native, no JS seek race on Safari.
    el.src = startAt > 0.05 ? `${st.url}#t=${startAt}` : st.url;
    const p = el.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => { try { el.onerror && el.onerror(); } catch (_) {} });
    }
    // Stall watchdog: if the stream hasn't actually advanced within 6s
    // (slow network, proxy hiccup, seek failure) revert to per-verse
    // clips so playback never silently freezes mid-chapter.
    setTimeout(() => {
      if (token !== cancelTokenRef.current || !streamingRef.current) return;
      if (el.currentTime > startAt + 0.5) return; // healthy
      streamingRef.current = false;
      streamRef.current = null;
      try { el.ontimeupdate = null; } catch (_) {}
      speakIndexHdRef.current(
        Math.max(idx, currentIndexRef.current), list, token, voiceId, model,
      );
    }, 6000);
    spokeOkRef.current += 1;
    return true;
  }, [clearNextReady]);
  switchToStreamRef.current = switchToStream;

  // Fire-and-forget: start building the stitched audio for an upcoming
  // chapter so the hands-free chapter hop starts near-instantly instead
  // of waiting on a cold synth. Readers call this as the current
  // chapter approaches its final verses.
  const prewarmChapter = useCallback((list) => {
    try {
      const effVoice = resolveEffVoice(hdVoice);
      if (!effVoice || !Array.isArray(list) || list.length < 2) return;
      api.post("/tts/chapter", {
        items: list.map((v) => ({ text: v.text, red: !!v.red })),
        voice: effVoice,
        model: "tts-1",
        speed: rate,
      }).catch(() => {});
    } catch (_) {}
  }, [resolveEffVoice, hdVoice, rate]);

  const speakIndexHd = useCallback(async (idx, list, token, voiceId, model) => {
    if (token !== cancelTokenRef.current) return;
    if (idx >= list.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoading(false);
      setCurrentIndex(0);
      // Clear any stale prefetches now that the session is done.
      prefetchRef.current = {};
      // Broadcast completion ONLY if something was actually heard —
      // see endSession for the runaway-scan rationale.
      if (spokeOkRef.current > 0) {
        // Keep the iOS audio session alive through the chapter hop.
        holdSessionWithSilence();
        try {
          window.dispatchEvent(new CustomEvent("hw:queue:ended", { detail: { kind: "hd" } }));
        } catch (_) {}
      } else {
        try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again."); } catch (_) {}
      }
      return;
    }
    // Show the spinner only while we're waiting on the FIRST verse —
    // subsequent verses are served from the prefetch cache so they
    // play instantly. Surfacing "Loading…" on every verse would make
    // the button flicker.
    if (idx === 0) setIsLoading(true);
    // Ask the backend to stitch this whole queue into ONE mp3 (once per
    // chain token). Synths are de-duplicated server-side with the
    // per-verse prefetches, so the stitch job doubles as an in-order
    // pipeline that warms every upcoming verse.
    if (streamReqRef.current !== token) {
      streamReqRef.current = token;
      requestChapterStreamRef.current(list, token, voiceId, model);
    }
    // Fetch the audio for `idx` — reuse a prefetch if it exists, else
    // start a fresh request. Either way we get a Blob back; turning
    // that into an audio element happens after we have the bytes so
    // playback starts the instant the data lands.
    const fetchVerse = (i) => {
      return api.post("/tts/speak", {
        text: list[i].text,
        voice: voiceId,
        model: model || "tts-1",
        speed: rate,
        reverent: !!list[i].red,
      }, { responseType: "blob", timeout: 25000 }).then((r) => r.data);
    };
    const key = `${token}:${idx}`;
    let blobPromise = prefetchRef.current[key];
    if (!blobPromise) {
      blobPromise = fetchVerse(idx);
      prefetchRef.current[key] = blobPromise;
    }
    // Kick off prefetch for the NEXT verse right now so by the time
    // this one ends, the next one's audio is already on disk, plus
    // materialize it for the synchronous iOS-background handoff.
    armNextRef.current(idx + 1, list, token, voiceId, model);
    let blob = null;
    try {
      blob = await blobPromise;
    } catch (_) { blob = null; }
    if (token !== cancelTokenRef.current) return;
    if (!blob) {
      // One retry before giving up — a single flaky response used to
      // be audible as "it skipped a couple of verses".
      delete prefetchRef.current[key];
      try { blob = await fetchVerse(idx); } catch (_) { blob = null; }
      if (token !== cancelTokenRef.current) return;
    }
    try {
      if (!blob) throw new Error("empty audio");
      const url = URL.createObjectURL(blob);
      // Tear down the previous element/url for this turn.
      teardownAudio();
      audioUrlRef.current = url;
      // ── Use the SINGLETON audio element instead of `new Audio()` ──
      // Why: mobile Safari and Chrome require play() to happen within
      // the user-gesture window. After a 3-5 s /tts/speak fetch, that
      // window has closed for any FRESHLY created element — so the
      // first verse plays but the next ones fail with NotAllowedError
      // and silently skip. The singleton was created on initial app
      // mount (via user interaction with the page) and carries its
      // gesture permission forever. This is the same pattern the
      // Daily Devotional / ReadAloudButton use, and it's why the
      // devotional reader never has this skipping bug.
      const el = getSingletonAudio();
      audioElRef.current = el;
      // Drop the prefetch entry for this idx now that we've consumed it.
      delete prefetchRef.current[key];
      // Detach any handlers from the previous verse so they don't fire
      // unexpectedly on this one's events.
      el.loop = false;
      el.ontimeupdate = null;
      el.onended = null;
      el.onerror = null;
      el.onended = () => {
        if (token !== cancelTokenRef.current) return;
        try { URL.revokeObjectURL(url); } catch (_) {}
        if (audioUrlRef.current === url) audioUrlRef.current = null;
        const nxt = idx + 1;
        setCurrentIndex(nxt);
        if (switchToStreamRef.current(nxt, list, token, voiceId, model)) return;
        const ready = nextReadyRef.current;
        if (ready && ready.idx === nxt && ready.url) {
          // Synchronous handoff — keeps iOS background audio alive.
          nextReadyRef.current = null;
          startVerseUrlRef.current(nxt, ready.url, list, token, voiceId, model);
        } else {
          speakIndexHd(nxt, list, token, voiceId, model);
        }
      };
      el.onerror = () => {
        if (token !== cancelTokenRef.current) return;
        // Skip the failed verse and continue — don't hard-stop the chapter
        // just because one TTS response or audio element hiccupped.
        try { URL.revokeObjectURL(url); } catch (_) {}
        if (audioUrlRef.current === url) audioUrlRef.current = null;
        if (noteVerseFailure()) {
          setIsPlaying(false); setIsPaused(false); setIsLoading(false);
          try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again."); } catch (_) {}
          return;
        }
        setCurrentIndex(idx + 1);
        speakIndexHd(idx + 1, list, token, voiceId, model);
      };
      // playOnce() stops whatever else is playing, swaps src, and
      // returns the play() promise. Because the singleton element
      // already has gesture permission from its creation moment,
      // this resolves successfully on mobile even after the long
      // fetch await above — which is the whole point of routing
      // through it instead of `new Audio()` each time.
      await playOnce(url);
      // Audio is actually playing now — drop the loading state so the
      // button switches from "Loading…" to "Stop".
      spokeOkRef.current += 1;
      failStreakRef.current = 0;
      setIsLoading(false);
    } catch (e) {
      // HD cloud call OR play() rejected (mobile autoplay block, etc.).
      // We used to fall back to the device's free voice via
      // `speechSynthesis.speak()`, but that fired on a SEPARATE audio
      // channel and overlapped with the next prefetched HD verse —
      // the "two voices" bug. New behavior: drop the cached prefetch
      // and skip to the next verse with the same HD voice.
      if (token !== cancelTokenRef.current) return;
      setIsLoading(false);
      delete prefetchRef.current[key];
      try { window.speechSynthesis.cancel(); } catch (_) {}
      if (noteVerseFailure()) {
        setIsPlaying(false); setIsPaused(false);
        try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again."); } catch (_) {}
        return;
      }
      setCurrentIndex(idx + 1);
      speakIndexHd(idx + 1, list, token, voiceId, model);
    }
  }, [rate, pickVoice, teardownAudio]);

  // Restart the ACTIVE session on the HD cloud voice from the current
  // verse. Used by the iOS "free voice first" watchdog + fatal native
  // errors so a dead device voice degrades to HD instead of silence.
  const switchSessionToHd = useCallback(() => {
    const sess = activeSessionRef.current;
    if (!sess || !sess.queue || sess.queue.length === 0) return false;
    setIosNativeVerdict("dead");
    try { window.speechSynthesis.cancel(); } catch (_) {}
    const idx = Math.max(0, Math.min(currentIndexRef.current, sess.queue.length - 1));
    const t2 = ++cancelTokenRef.current;
    spokeOkRef.current = 0;
    failStreakRef.current = 0;
    setIsPlaying(true);
    setIsPaused(false);
    setIsLoading(true);
    setCurrentIndex(idx);
    speakIndexHd(idx, sess.queue, t2, IOS_FALLBACK_HD_VOICE, sess.options?.model);
    return true;
  }, [speakIndexHd]);

  const speakIndex = useCallback((idx, list, langVoice, token) => {
    if (token !== cancelTokenRef.current) return;
    if (idx >= list.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentIndex(0);
      // Same auto-advance broadcast as the HD path — but only when the
      // chapter was actually HEARD (see runaway-scan guard).
      if (spokeOkRef.current > 0) {
        try {
          window.dispatchEvent(new CustomEvent("hw:queue:ended", { detail: { kind: "device" } }));
        } catch (_) {}
      } else {
        try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again, or pick a different voice in the player."); } catch (_) {}
      }
      return;
    }
    const u = new window.SpeechSynthesisUtterance(list[idx].text);
    u.lang = langVoice || "en-US";
    // Reverent pace: verses carrying divine speech (red letters) read
    // slightly slower than the surrounding narration.
    u.rate = list[idx].red ? rate * 0.9 : rate;
    const v = pickVoice(langVoice);
    if (v) u.voice = v;
    u.onstart = () => {
      if (token !== cancelTokenRef.current) return;
      speechStartedRef.current = true;
      if (IOS_STANDALONE_HD_FALLBACK) setIosNativeVerdict("ok");
    };
    u.onend = () => {
      if (token !== cancelTokenRef.current) return;
      // Verse actually finished speaking — mark real audio success so
      // the end-of-queue broadcast (auto-advance) is allowed to fire.
      spokeOkRef.current += 1;
      failStreakRef.current = 0;
      setCurrentIndex(idx + 1);
      if (!window.speechSynthesis.paused) speakIndex(idx + 1, list, langVoice, token);
    };
    u.onerror = (event) => {
      if (token !== cancelTokenRef.current) return;
      // Mobile browsers (esp. Chrome/Safari on Android & iOS) fire "interrupted",
      // "canceled" and "audio-busy" errors when the system audio focus shifts,
      // when the screen locks, or when speak() is called too quickly back-to-back.
      // Treat these as transient — skip the failed verse and continue reading
      // instead of stopping the whole chapter. Only treat truly fatal errors
      // ("synthesis-failed", "synthesis-unavailable", "language-unavailable",
      //  "voice-unavailable", "not-allowed") as a stop signal.
      const err = (event && event.error) || "";
      const fatal = new Set([
        "synthesis-failed", "synthesis-unavailable",
        "language-unavailable", "voice-unavailable",
        "not-allowed", "network",
      ]);
      if (fatal.has(err)) {
        // iOS home-screen: a fatal free-voice error means this device
        // can't do native TTS — hop to HD instead of going silent.
        if (IOS_STANDALONE_HD_FALLBACK && !hdVoice && switchSessionToHd()) return;
        setIsPlaying(false);
        setIsPaused(false);
        if (spokeOkRef.current === 0) {
          try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again, or pick a different voice in the player."); } catch (_) {}
        }
        return;
      }
      // Transient — advance to the next verse so the read-aloud keeps
      // going. But if NOTHING has spoken yet and errors keep streaking,
      // the engine is dead on this device: stop instead of silently
      // scanning through the whole chapter (iPhone runaway-scan bug).
      if (noteVerseFailure()) {
        if (IOS_STANDALONE_HD_FALLBACK && !hdVoice && switchSessionToHd()) return;
        setIsPlaying(false);
        setIsPaused(false);
        try { toast.error("Audio couldn't play on this device. Try tapping Read aloud again, or pick a different voice in the player."); } catch (_) {}
        return;
      }
      setCurrentIndex(idx + 1);
      speakIndex(idx + 1, list, langVoice, token);
    };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, [rate, pickVoice, hdVoice, switchSessionToHd]);
  // Forward-ref wiring so setHdVoice (defined above) can call us.
  speakIndexRef.current = speakIndex;
  speakIndexHdRef.current = speakIndexHd;

  // play(textList, meta, languageCode, voiceLocale, options{translate})
  // - If translate=true and languageCode !== "en", server-translates each item before queueing.
  //
  // Latency strategy (Feb 2026): the old behavior translated the
  // ENTIRE chapter before the first verse could play — 3-30 s of
  // dead air on a cold-cache language switch. New flow:
  //   1. Translate verse 1 ONLY (1 string) — usually <1 s with the
  //      shared text-translation cache.
  //   2. Start TTS on verse 1 immediately. The user hears audio in
  //      ~2 s instead of waiting for the whole chapter to translate.
  //   3. While verse 1 plays, translate the REST of the chapter as
  //      one batched POST in the background. As each translation
  //      lands, we replace the queue item in place so verses 2+ play
  //      in the right language.
  //   4. If the user's network is fast enough, the bulk translation
  //      lands before verse 1 ends — they never even notice it
  //      happened in two passes.
  const play = useCallback(async (newQueue, newMeta, langCode, langVoice, options = {}) => {
    stop();
    // MUST run synchronously inside the user's tap, before any await —
    // see speechPrimedRef. Runs AFTER stop() so its cancel() doesn't
    // wipe the prime; the silent utterance also buffers the real first
    // verse away from cancel() (iOS swallows a speak() that directly
    // follows a cancel()).
    try {
      const ss = window.speechSynthesis;
      if (ss && !speechPrimedRef.current) {
        const pu = new SpeechSynthesisUtterance(" ");
        pu.volume = 0;
        ss.speak(pu);
        speechPrimedRef.current = true;
      }
    } catch (_) {}
    const token = ++cancelTokenRef.current;
    setMeta(newMeta);
    setLanguage(langCode);

    const needsTx = options.translate && langCode && langCode !== "en";
    let listToSpeak = newQueue;

    if (needsTx && newQueue.length > 0) {
      // Phase 1 — translate just verse 1 so playback can start ASAP.
      // 4s ceiling: if our translation cache is cold and Claude is
      // slow, we'd rather start reading in English than make the
      // user stare at a spinner. The full chapter still gets
      // translated in Phase 2 below.
      try {
        const { data } = await api.post("/translate-text", {
          texts: [newQueue[0].text],
          lang: langCode,
        }, { timeout: 4000 });
        if (token !== cancelTokenRef.current) return;
        const first = (data && data.translations && data.translations[0]) || newQueue[0].text;
        listToSpeak = newQueue.map((q, i) => (i === 0 ? { ...q, text: first } : q));
      } catch {
        // Couldn't even translate verse 1 — fall back to English so
        // the user at least hears SOMETHING within the gesture window.
        listToSpeak = newQueue;
      }
    }
    if (token !== cancelTokenRef.current) return;
    spokeOkRef.current = 0;
    failStreakRef.current = 0;
    setQueue(listToSpeak);
    setIsPlaying(true);
    setIsPaused(false);
    setIsLoading(true);
    setCurrentIndex(0);
    try { window.dispatchEvent(new CustomEvent("hw:audio:start")); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "chapter-reader" } })); } catch (_) {}
    activeSessionRef.current = {
      queue: listToSpeak, langCode, langVoice: langVoice || "en-US", options,
    };
    // iOS standalone: only force the HD cloud path when this device has
    // PROVEN its free voice doesn't work (verdict "dead"). Otherwise we
    // try the free device voice first — the watchdog below flips to HD
    // mid-session if nothing actually speaks.
    const effVoice = resolveEffVoice(hdVoice);
    if (effVoice) {
      // Prefetch verse 0 in the background BEFORE we call speakIndexHd
      // so the function picks the cached promise up by `${token}:0`
      // rather than firing a second identical request.
      const firstKey = `${token}:0`;
      if (!prefetchRef.current[firstKey] && listToSpeak[0]) {
        prefetchRef.current[firstKey] = api.post("/tts/speak", {
          text: listToSpeak[0].text,
          voice: effVoice,
          model: options.model || "tts-1",
          speed: rate,
          reverent: !!listToSpeak[0].red,
        }, { responseType: "blob", timeout: 25000 }).then((r) => r.data).catch(() => null);
      }
      speakIndexHd(0, listToSpeak, token, effVoice, options.model);
    } else {
      setIsLoading(false);
      speakIndex(0, listToSpeak, langVoice || "en-US", token);
      // iOS-standalone watchdog: if the free voice hasn't ACTUALLY
      // started speaking within 3s, mark this device dead and restart
      // the session on HD from the current verse. Runs every session
      // (cheap) so a regression re-triggers the fallback automatically.
      if (IOS_STANDALONE_HD_FALLBACK) {
        speechStartedRef.current = false;
        setTimeout(() => {
          if (token !== cancelTokenRef.current) return;
          if (speechStartedRef.current || spokeOkRef.current > 0) return;
          setIosNativeVerdict("dead");
          try { window.speechSynthesis.cancel(); } catch (_) {}
          const sess = activeSessionRef.current;
          if (!sess || !sess.queue || sess.queue.length === 0) return;
          const idx = Math.max(0, Math.min(currentIndexRef.current, sess.queue.length - 1));
          const t2 = ++cancelTokenRef.current;
          setIsLoading(true);
          speakIndexHd(idx, sess.queue, t2, IOS_FALLBACK_HD_VOICE, sess.options?.model);
        }, 3000);
      }
    }

    // Phase 2 — background-translate verses 2..N while audio plays.
    // Replace queue items in place as each translation resolves.
    if (needsTx && newQueue.length > 1) {
      (async () => {
        try {
          const rest = newQueue.slice(1).map((q) => q.text);
          const { data } = await api.post("/translate-text", {
            texts: rest,
            lang: langCode,
          });
          if (token !== cancelTokenRef.current) return;
          const tx = (data && data.translations) || [];
          // Patch the live queue and the activeSession so the
          // speakIndexHd loop reads the translated text for verses
          // 2..N when it advances to them.
          setQueue((prev) => {
            const next = prev.slice();
            for (let i = 0; i < rest.length; i++) {
              const tgt = i + 1;
              if (next[tgt]) {
                next[tgt] = { ...next[tgt], text: tx[i] || next[tgt].text };
              }
            }
            // Update active session queue so speakIndexHd's closure
            // sees the new text on its next call (it reads from
            // `list` arg, which it captured by reference).
            const sess = activeSessionRef.current;
            if (sess && sess.queue) {
              for (let i = 0; i < rest.length; i++) {
                const tgt = i + 1;
                if (sess.queue[tgt]) {
                  sess.queue[tgt] = { ...sess.queue[tgt], text: tx[i] || sess.queue[tgt].text };
                }
              }
            }
            return next;
          });
        } catch (_) {
          // Background translation failed — verses 2+ will play in
          // English. Not ideal but better than the user staring at a
          // spinner because the bulk translate timed out.
        }
      })();
    }
  }, [stop, speakIndex, speakIndexHd, switchSessionToHd, hdVoice, rate]);

  const pause = useCallback(() => {
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch (_) {}
    } else {
      try { window.speechSynthesis.pause(); } catch (_) {}
    }
    setIsPaused(true);
  }, []);

  // Re-cue narration from an ABSOLUTE index in the active session
  // queue. Used as a fallback when a paused clip can no longer be
  // resumed natively (audio element torn down after navigation, blob
  // revoked, or mobile speechSynthesis.resume() silently no-ops). We
  // keep `currentIndex` absolute by replaying the FULL session queue
  // starting at `idx` (speakIndex(Hd) accept a start index) rather
  // than slicing — so the AudioBar verse/progress stays correct.
  const recueFrom = useCallback((absIdx) => {
    const session = activeSessionRef.current;
    if (!session || !session.queue || session.queue.length === 0) return false;
    const idx = Math.max(0, Math.min(absIdx || 0, session.queue.length - 1));
    cancelTokenRef.current += 1;
    // cancel() clears the utterance queue but can leave the engine in
    // a PAUSED state — where any new speak() is silently swallowed
    // (the classic mobile Web-Speech bug). resume() un-corks it so the
    // re-cued verse actually speaks.
    try { window.speechSynthesis.cancel(); window.speechSynthesis.resume(); } catch (_) {}
    teardownAudio();
    prefetchRef.current = {};
    setIsPlaying(true);
    setIsPaused(false);
    setCurrentIndex(idx);
    const token = ++cancelTokenRef.current;
    const effVoice = resolveEffVoice(hdVoice);
    if (effVoice) {
      setIsLoading(true);
      speakIndexHd(idx, session.queue, token, effVoice, session.options?.model);
    } else {
      speakIndex(idx, session.queue, session.langVoice || "en-US", token);
    }
    return true;
  }, [hdVoice, speakIndexHd, speakIndex, teardownAudio]);

  const resume = useCallback(() => {
    setIsPaused(false);
    const el = audioElRef.current;
    const hasClip = el && el.src && !el.ended;
    if (hasClip) {
      // HD (cloud) path — resume the exact paused clip. Verify it
      // actually started; if not (element suspended after navigating /
      // screen-off), re-cue from the current verse. Stream mode is
      // exempt from the quick liveness check: a large chapter file can
      // legitimately take >350ms to rebuffer, and re-cueing it caused
      // the "plays then pauses then restarts" glitch on iPhones.
      try {
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (_) {}
      if (!streamingRef.current) {
        setTimeout(() => {
          const a = audioElRef.current;
          const live = a && a.src && !a.paused && !a.ended;
          if (!live) recueFrom(currentIndexRef.current);
        }, 350);
      }
      return;
    }
    // Device (Web Speech) path — speechSynthesis.pause()/resume() is
    // unreliable on mobile and often can't be resumed at all. Rather
    // than trust it, always re-cue the current verse so pressing Play
    // reliably restarts audio.
    recueFrom(currentIndexRef.current);
  }, [recueFrom]);

  // Jump to a specific verse index in the active queue. Used by the
  // voice commander for "repeat that verse", "go back five verses",
  // and "jump to verse twelve". Clamps to [0, queue.length-1].
  const jumpTo = useCallback((targetIdx) => {
    const session = activeSessionRef.current;
    if (!session || !session.queue || session.queue.length === 0) return;
    const idx = Math.max(0, Math.min(targetIdx, session.queue.length - 1));
    // Cancel everything in flight, then re-cue from the new index. We
    // reuse the same `re-cue from current` pattern as the voice-change
    // effect below — slice the queue from `idx`, set activeSessionRef
    // to that slice, fire speakIndex(Hd)(0, ...).
    cancelTokenRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
    const remaining = session.queue.slice(idx);
    if (remaining.length === 0) return;
    setIsPlaying(true);
    setIsPaused(false);
    setCurrentIndex(idx);
    const token = ++cancelTokenRef.current;
    activeSessionRef.current = { ...session, queue: remaining };
    const effVoice = resolveEffVoice(hdVoice);
    if (effVoice) {
      speakIndexHd(0, remaining, token, effVoice, session.options?.model);
    } else {
      speakIndex(0, remaining, session.langVoice || "en-US", token);
    }
  }, [hdVoice, speakIndex, speakIndexHd, teardownAudio]);

  useEffect(() => () => {
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
  }, [teardownAudio]);

  // Cross-context audio bus — when the user starts the background
  // gospel mix from anywhere in the app, stop the Bible read-aloud so
  // the two don't overlap (the "double voices" bug).
  useEffect(() => {
    const onBgStart = () => {
      cancelTokenRef.current += 1;
      try { window.speechSynthesis.cancel(); } catch (_) {}
      teardownAudio();
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoading(false);
    };
    window.addEventListener("hw:bgmusic:start", onBgStart);
    return () => window.removeEventListener("hw:bgmusic:start", onBgStart);
  }, [teardownAudio]);

  // External-start bus — any OTHER speech source (Read.jsx verse-of-
  // the-day, voiceCommander confirmations, future TTS surfaces) fires
  // `hw:speech:external-start` right before they call speak(). We
  // hard-stop our own playback so two voices can never overlap.
  useEffect(() => {
    const onExternalStart = () => stop();
    window.addEventListener("hw:speech:external-start", onExternalStart);
    return () => window.removeEventListener("hw:speech:external-start", onExternalStart);
  }, [stop]);

  // Stop-all bus — the Voice Commander fires `hw:stop-all-media` right
  // before it starts NEW playback ("play Lecrae" while a video runs).
  // We hard-stop our TTS/audio so nothing talks over the new video.
  useEffect(() => {
    const onStopAll = () => stop();
    window.addEventListener("hw:stop-all-media", onStopAll);
    return () => window.removeEventListener("hw:stop-all-media", onStopAll);
  }, [stop]);

  // Read-aloud bus — single-card ReadAloudButton, the Today devotional
  // and the morning briefing all fire `hw:read-aloud:start` before
  // they start their singleton audio. If the chapter reader is the
  // one that fired the event we ignore it (we're the source); for
  // any other source we hard-stop chapter playback so two voices
  // can't talk over each other.
  useEffect(() => {
    const onReadAloudStart = (e) => {
      if (e?.detail?.id === "chapter-reader") return;
      stop();
    };
    window.addEventListener("hw:read-aloud:start", onReadAloudStart);
    return () => window.removeEventListener("hw:read-aloud:start", onReadAloudStart);
  }, [stop]);


  // When the user picks a different voice mid-playback, re-cue from the
  // *current* verse with the newly-selected voice so the change applies
  // immediately. Without this, the change would only take effect when
  // the next verse fires its `onend` callback — which feels like the
  // voice setting "isn't doing anything". We rely on `currentIndex` to
  // know where to resume, and `activeSessionRef` for the queue/lang.
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  const initialVoiceSig = useRef(`${voiceURI || ""}|${hdVoice || ""}`);
  useEffect(() => {
    const sig = `${voiceURI || ""}|${hdVoice || ""}`;
    // Skip the first run — we don't want to "re-cue" on initial mount.
    if (initialVoiceSig.current === sig) return;
    initialVoiceSig.current = sig;
    const session = activeSessionRef.current;
    if (!session || !isPlaying) return;
    const resumeFrom = Math.max(0, Math.min(currentIndexRef.current, session.queue.length - 1));
    const remaining = session.queue.slice(resumeFrom);
    if (remaining.length === 0) return;
    // Cancel current speech, then re-start from current verse with new voice.
    cancelTokenRef.current += 1;
    try { window.speechSynthesis.cancel(); } catch (_) {}
    teardownAudio();
    const token = ++cancelTokenRef.current;
    setCurrentIndex(resumeFrom);
    const effVoice = resolveEffVoice(hdVoice);
    if (effVoice) {
      speakIndexHd(0, remaining, token, effVoice, session.options?.model);
    } else {
      speakIndex(0, remaining, session.langVoice, token);
    }
    // Keep the visible "current verse" index aligned with the original
    // queue rather than the resumed slice. We do that by re-mapping
    // updates after we've kicked off — speakIndex/speakIndexHd advance
    // a *local* idx but always overwrite setCurrentIndex with that idx;
    // for the user-visible position to make sense we offset on completion.
    // Simpler: we rebuild the active session so the slice IS the source.
    activeSessionRef.current = { ...session, queue: remaining };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceURI, hdVoice]);

  // Media Session — wires Bible read-aloud playback into the phone's
  // lock screen / notification shade widget. Title shows the current
  // verse reference (e.g. "Genesis 1:3"), body shows the verse text
  // itself, artwork is the Holy Words logo. Buttons on the widget
  // map directly to our verse navigation so the reader can move
  // through scripture without unlocking the phone — same UX as the
  // user's YouTube lock-screen example.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    if (!isPlaying || queue.length === 0) return;
    const verse = queue[currentIndex] || {};
    const refLabel = meta?.book && meta?.chapter
      ? `${meta.book} ${meta.chapter}${verse.verse ? `:${verse.verse}` : ""}`
      : (meta?.title || "Holy Words");
    const verseText = (verse.text || "").slice(0, 220);
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: refLabel,
        artist: verseText || "Holy Words",
        album: "Holy Words · Bible",
        artwork: [
          { src: "/icons/holywords-192.png", sizes: "192x192", type: "image/png" },
          { src: "/icons/holywords-512.png", sizes: "512x512", type: "image/png" },
        ],
      });
    } catch (_) {}
    try { navigator.mediaSession.playbackState = isPaused ? "paused" : "playing"; } catch (_) {}
    const handlers = {
      play: () => { resume(); },
      pause: () => { pause(); },
      stop: () => { stop(); },
      previoustrack: () => { jumpTo(Math.max(0, currentIndexRef.current - 1)); },
      nexttrack: () => { jumpTo(Math.min((activeSessionRef.current?.queue?.length || 1) - 1, currentIndexRef.current + 1)); },
      seekbackward: (e) => { jumpTo(Math.max(0, currentIndexRef.current - Math.max(1, Math.round((e?.seekOffset || 10) / 8)))); },
      seekforward:  (e) => { jumpTo(Math.min((activeSessionRef.current?.queue?.length || 1) - 1, currentIndexRef.current + Math.max(1, Math.round((e?.seekOffset || 10) / 8)))); },
    };
    Object.entries(handlers).forEach(([action, fn]) => {
      try { navigator.mediaSession.setActionHandler(action, fn); } catch (_) {}
    });
    return () => {
      Object.keys(handlers).forEach((action) => {
        try { navigator.mediaSession.setActionHandler(action, null); } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isPaused, currentIndex, queue, meta]);

  // Voice-bus listener for read-mode controls. Lets users say things
  // like "repeat that verse" / "go back 5 verses" / "speak slower"
  // while a chapter is being read aloud. Verbs handled here are
  // exclusive to the audio Bible — player modals handle music verbs.
  useEffect(() => {
    const onCmd = (e) => {
      const verb = e?.detail?.verb;
      const amount = e?.detail?.amount;
      if (!verb) return;
      // Many of these are noops if the audio Bible isn't playing —
      // that's intentional, the verb may be picked up by another bus
      // listener (a player modal) instead.
      const session = activeSessionRef.current;
      const queueLen = session?.queue?.length || 0;
      if (!queueLen) return;
      const idx = currentIndexRef.current;
      switch (verb) {
        case "verse_repeat":
          jumpTo(idx);
          break;
        case "verse_prev":
          jumpTo(Math.max(0, idx - Math.max(1, amount || 1)));
          break;
        case "verse_next":
          jumpTo(Math.min(queueLen - 1, idx + Math.max(1, amount || 1)));
          break;
        case "verse_first":
          jumpTo(0);
          break;
        case "verse_last":
          jumpTo(queueLen - 1);
          break;
        case "verse_seek": {
          // 1-indexed verse number from the user's mouth → 0-indexed.
          const target = Math.max(0, (amount || 1) - 1);
          jumpTo(target);
          break;
        }
        case "rate_faster":
          setRate((r) => Math.min(2.0, +(r + 0.15).toFixed(2)));
          break;
        case "rate_slower":
          setRate((r) => Math.max(0.5, +(r - 0.15).toFixed(2)));
          break;
        case "rate_normal":
          setRate(1.0);
          break;
        case "audio_pause":
          pause();
          break;
        case "audio_resume":
          resume();
          break;
        case "audio_stop":
          stop();
          break;
        default:
          break;
      }
    };
    window.addEventListener("hw-voice-command", onCmd);
    return () => window.removeEventListener("hw-voice-command", onCmd);
  }, [jumpTo, pause, resume, stop]);

  const supportedLangCodes = LANGUAGES.map((l) => l.voice.slice(0, 2).toLowerCase());
  const filteredVoices = voices.filter((v) => supportedLangCodes.includes((v.lang || "").slice(0, 2).toLowerCase()));

  return (
    <AudioContext.Provider value={{
      language, setLanguage, isPlaying, isPaused, isLoading, currentIndex,
      queue, meta, rate, setRate,
      voices: filteredVoices, voiceURI, setVoiceURI,
      hdVoice, setHdVoice,
      prewarmChapter,
      sleepPreset, setSleepTimer,
      play, pause, resume, stop, close, jumpTo, cancelAllSpeech,
    }}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  return useContext(AudioContext);
}
